import React, { useEffect, useMemo, useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { gqlGetCpGroup, gqlAddCpGroup, gqlUpdateCpGroup, gqlDeleteCpGroup } from './graphql';
import { useParams, useLocation, generatePath, useHistory } from 'react-router-dom';
import {
  FieldInput,
  YupStringReq,
  FormCard,
  FormCol,
  FormRow,
  isNumeric,
  promiseModalDialog,
  toastSuccess,
  UserTooltip,
} from 'motion-components';

import * as Yup from 'yup';
import * as Lo from 'lodash';
import { getRouteByName } from 'app/routes';
import { useHandleError } from 'shared-pages/services';

const formShape = {
  name: YupStringReq,
};

const validationSchema = Yup.object().shape(formShape);
const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
type FormValuesType = Yup.InferType<typeof validationSchema>;

export const CpGroupPage = () => {
  const { handleGraphqlError } = useHandleError('cpgroup');
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;

  const newRecord = useMemo<FormValuesType>(
    () =>
      ({
        id: -1,
        name: null,
      } as FormValuesType),
    [],
  );

  const [addRecord] = useMutation(gqlAddCpGroup);
  const [updateRecord] = useMutation(gqlUpdateCpGroup);
  const [deleteRecord] = useMutation(gqlDeleteCpGroup);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetCpGroup);
  const data = isNew ? newRecord : rawData ? rawData.getCpGroupById : undefined;

  /* fetch data */
  useEffect(() => {
    if (!isNew) {
      get({ variables: { id: idNum } });
    }
  }, [get, id, idNum, isNew]);

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = (values: FormValuesType, actions) => {
    actions.setSubmitting(false);

    // Αν είναι νέα εγγραφή τοτε γράφω όλα τα πεδία
    if (isNew) {
      console.log('[CpGroup] New:', values);
      addRecord({ variables: { data: { ...values } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Προσθήκη Εγγραφής [${d.data.addCpGroup.id}]`);
          handleReturn();
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
        });
    } else {
      // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
      console.log('[CpGroup] Update:', values);
      updateRecord({ variables: { data: { ...values, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Ενημέρωση Εγγραφής [${d.data.updateCpGroup.id}]`);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.push(generatePath(getRouteByName('CpGroups'))))
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);
  // Handler να πάει στην πίσω σελίδα
  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('CpGroups')), { ...(location as any).state });
  }, [history, location.state]);

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <FormCard
        isNew={isNew}
        title={isNew ? 'Κατηγορία: Εισαγωγή Νέας' : `Κατηγορία: ${data?.name}`}
        onSubmit={handleSubmit}
        onReturn={null}
        onDelete={handleDelete}
        permitDelete={true}
        initialValues={pickProperties(data)}
        validationSchema={validationSchema}
        enableReinitialize
        idx={idNum}
      >
        <FormRow>
          <GroupName />
        </FormRow>
        <div className="float-end">{data && !isNew && <UserTooltip data={data} />}</div>
      </FormCard>
    );
  }
};

// ---------------------- DATA FIELDS -----------------------
const GroupName = () => {
  return <FieldInput as={FormCol} label="Κατηγορία" name="name" placeholder="- κατηγορία -" />;
};

